import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Chip,
  InputAdornment,
  Avatar
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  CalendarMonth, 
  ArrowForward, 
  CheckCircle, 
  AttachMoney,
  AccessTime
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedContainer, AnimatedButton, TextReveal } from '../common/AnimatedComponents';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { logFunnelEvent } from '../../firebase/analytics';

const SmartStartBookingHero = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Wizard State
  const [step, setStep] = useState(1); // 1: Business Info, 2: Service Info, 3: Preview/CTA
  const [formData, setFormData] = useState({
    businessName: '',
    industry: 'consulting',
    serviceName: '',
    duration: 60,
    price: 100
  });

  const industries = [
    { value: 'consulting', label: 'Consulting & Coaching' },
    { value: 'beauty', label: 'Beauty & Wellness' },
    { value: 'medical', label: 'Health & Medical' },
    { value: 'service', label: 'Home Services' },
    { value: 'fitness', label: 'Fitness & Training' }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNext = () => {
    if (step === 1 && formData.businessName) {
      logFunnelEvent('demo_intent', { demo: 'booking', step: 1 });
      setStep(2);
    } else if (step === 2 && formData.serviceName) {
      logFunnelEvent('demo_engagement', { demo: 'booking' });
      setStep(3);
    }
  };

  const handleSaveAndSignup = () => {
    logFunnelEvent('demo_cta_click', { demo: 'booking' });
    sessionStorage.setItem('bookingStartData', JSON.stringify({
      ...formData,
      timestamp: Date.now()
    }));
    navigate(ROUTES.SIGNUP);
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 8, pb: 12, position: 'relative', zIndex: 1 }}>
      <Grid container spacing={{ xs: 3, md: 8 }} alignItems="center" sx={{ minHeight: '70vh' }}>
        
        {/* Left Side: Copy */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="The New Standard for Service Pros"
              sx={{ 
                mb: 3, 
                fontWeight: 600,
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: theme.palette.primary.light
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '3rem', md: '4rem', lg: '4.5rem' },
                fontWeight: 800,
                lineHeight: 1,
                mb: 3,
                color: '#ffffff',
                letterSpacing: '-0.04em',
              }}
            >
              You didn't go freelance
              <br />
              <Typography
                component="span"
                variant="inherit"
                sx={{
                  color: theme.palette.primary.main,
                  background: 'linear-gradient(to right, #60a5fa, #2563eb)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                to chase no-shows.
              </Typography>
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: theme.palette.text.secondary,
                mb: 5,
                lineHeight: 1.6,
                fontWeight: 400,
                maxWidth: 540,
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}
            >
              FutureSense is the AI-first business manager that handles your bookings, reminders, and payments — so you can focus on the craft that actually pays.
            </Typography>

            <Stack spacing={2}>
              {['Replaces Calendly, QuickBooks & Spreadsheets', 'AI churn detection & automatic re-engagement', 'Free core tools, grow when you scale'].map((text) => (
                <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>{text}</Typography>
                </Box>
              ))}
            </Stack>
          </motion.div>
        </Grid>

        {/* Right Side: Smart Start Wizard */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Card 
              sx={{ 
                ...theme.customEffects?.glass,
                p: { xs: 2, md: 4 },
                position: 'relative',
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: -1,
                  padding: '1px',
                  borderRadius: 'inherit',
                  background: 'linear-gradient(to bottom right, rgba(255,255,255,0.2), rgba(255,255,255,0))',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  pointerEvents: 'none',
                }
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                        Setup Your Booking Page
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
                        See your business live in 60 seconds.
                      </Typography>
                      
                      <Stack spacing={3}>
                        <TextField
                          fullWidth
                          label="Business Name"
                          name="businessName"
                          value={formData.businessName}
                          onChange={handleInputChange}
                          placeholder="e.g. Acme Consulting"
                        />
                        
                        <TextField
                          select
                          fullWidth
                          label="Industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleInputChange}
                        >
                          {industries.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          onClick={handleNext}
                          disabled={!formData.businessName}
                          endIcon={<ArrowForward />}
                          sx={{ py: 1.5, fontSize: '1.05rem' }}
                        >
                          See My Booking Page in 60 Seconds
                        </Button>
                      </Stack>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                        Create First Service
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
                        What can your clients book?
                      </Typography>

                      <Stack spacing={3}>
                        <TextField
                          fullWidth
                          label="Service Name"
                          name="serviceName"
                          value={formData.serviceName}
                          onChange={handleInputChange}
                          placeholder="e.g. Initial Consultation"
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            fullWidth
                            label="Duration (min)"
                            name="duration"
                            type="number"
                            value={formData.duration}
                            onChange={handleInputChange}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><AccessTime sx={{ color: 'white' }} /></InputAdornment>,
                            }}
                          />
                          <TextField
                            fullWidth
                            label="Price ($)"
                            name="price"
                            type="number"
                            value={formData.price}
                            onChange={handleInputChange}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><AttachMoney sx={{ color: 'white' }} /></InputAdornment>,
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button 
                            variant="text" 
                            onClick={() => setStep(1)}
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            Back
                          </Button>
                          <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handleNext}
                            disabled={!formData.serviceName}
                            endIcon={<CalendarMonth />}
                            sx={{ py: 1.5 }}
                          >
                            Create Preview
                          </Button>
                        </Box>
                      </Stack>
                    </motion.div>
                  )}

                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Box sx={{ 
                        mb: 4, 
                        p: 3, 
                        borderRadius: 3, 
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.primary.light, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <CheckCircle fontSize="small" /> Booking Page Preview
                        </Typography>
                        
                        <Box sx={{ bgcolor: '#ffffff', p: 3, borderRadius: 2, color: '#000000' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                             <Typography variant="subtitle1" fontWeight="800">{formData.businessName}</Typography>
                             <Avatar sx={{ width: 28, height: 28, fontSize: 14, bgcolor: theme.palette.primary.main }}>{formData.businessName[0]}</Avatar>
                          </Box>
                          <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderColor: '#e2e8f0', transform: 'none !important', boxShadow: 'none !important' }}>
                              <Typography variant="subtitle2" fontWeight="700">{formData.serviceName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formData.duration} min • ${formData.price}
                              </Typography>
                          </Card>
                          <Button size="small" variant="contained" fullWidth sx={{ borderRadius: 1.5 }}>Book Now</Button>
                        </Box>
                      </Box>

                      <Typography variant="body1" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 500 }}>
                        System ready! Create your account to go live.
                      </Typography>

                      <Stack spacing={2}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          onClick={handleSaveAndSignup}
                          endIcon={<ArrowForward />}
                          sx={{ 
                            py: 1.8, 
                            fontSize: '1.1rem',
                            background: theme.palette.success.main,
                            '&:hover': { background: theme.palette.success.dark }
                          }}
                        >
                          Save & Go Live
                        </Button>
                        
                        <Button 
                          fullWidth
                          variant="text" 
                          onClick={() => setStep(2)}
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          Edit Configuration
                        </Button>
                      </Stack>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SmartStartBookingHero;
